// Checkout (spec/13 §4, F-POS-12): the sale's total and the tender that pays it, in a centred dialog sized so a normal
// sale is taken without scrolling at 1366×768 (the owner's 2026-10-08 rework). One line of figures (the total large,
// subtotal and GST quiet beside it), the payment method as a segmented control, and for cash "Cash received" with
// one-tap amounts and the change worked out in place. The amount is the outstanding balance and stays out of the way;
// "Split payment" shows it, to take part now and the rest another way. A business's GSTIN, a discount code and a
// coupon are small links that open their field in place.
//
// **The change** (ADR-0036, ADR-0049): when the cash handed over is more than the amount, checkout offers "Give it back in
// cash" (the default) or "Add it to a player's wallet". On a sale with a player the wallet is theirs; on an anonymous
// sale staff pick the player right here, and the sale itself stays as it was. Where the venue cannot take change into a
// wallet (the wallet off, or top-ups taxed at sale) the choice is shown switched off, with the setting to change.
//
// The server prices, records and settles every payment; this only chooses the tender and says what was handed over.

import type { Money, OrganizationPlayer, PaymentIntent, Sale, SaleTender } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import {
  Avatar,
  Button,
  CheckIcon,
  cx,
  Dialog,
  encodeQr,
  Impact,
  qrPath,
  RollingNumber,
  SegmentedControl,
  Skeleton,
  StepSlide,
  TextField,
  useShake,
  useToast,
} from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useCallback, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem, type ProblemReader } from '../../api/use-problem';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useConsoleScope } from '../../shell/scope';
import { Notice, SearchField } from '../../ui';
import { cashSuggestions, changeDue, minorToMajor, moneyFrom } from '../money';
import { GuestTicketCodes } from '../guests/GuestTicket';
import { OnlinePayment } from '../payments/OnlinePayment';
import { useCreateSalePaymentIntent, useOnlinePayments } from '../payments/queries';
import { pickerResults, PLAYER_SEARCH_MIN, playerName, usePlayerSearch } from '../players/queries';
import { useWalletBalance } from '../wallet/queries';
import { CashLockedNotice, useCashLocked } from '../shifts/shift-prompt';
import { counterUpiIntent, useApplySaleCode, useCounterUpi, useRedeemCoupon, useSetSaleBuyer, useTakeSaleOnAccount, useTakeSalePayment } from './queries';

/**
 * A checkout tender: the immediate sale tenders, plus on account, which is taken through its own door,
 * and online, which is a payment request the player pays through the provider (F-PAY-03) — offered only
 * when the venue's online gates are open, and recorded only when the provider confirms it.
 */
type CheckoutTender = SaleTender | 'on_account' | 'online';

/** The venue's own tenders (spec/06 §9.1): cash, its card machine (F-PAY-02) and its UPI QR. */
const COUNTER_TENDERS: readonly CheckoutTender[] = ['cash', 'counter_card', 'counter_upi'];

const TENDER_ICON: Record<CheckoutTender, string> = { cash: 'cash', counter_card: 'card', counter_upi: 'qr', wallet: 'wallet', on_account: 'orders', online: 'bolt' };

/** A tender's glyph: the wallet and on account are the navigation's own (their screens'), the rest interface glyphs. */
function TenderIcon({ tender }: { readonly tender: CheckoutTender }): ReactNode {
  return tender === 'wallet' || tender === 'on_account' ? <NavIcon name={TENDER_ICON[tender]} /> : <Icon name={TENDER_ICON[tender]} />;
}

/** Where a cash overpayment's change goes. */
type ChangeTo = 'cash' | 'wallet';

/**
 * A refused tender, in plain words: the catalogue's copy for its code (the wallet short or off, on account off or over
 * the limit, a card number, change that cannot go to the wallet), and the sale's own line for a plain conflict.
 */
function refusalMessage(t: (key: string) => string, problem: ProblemReader, error: unknown): string {
  if (problemCode(error) === PROBLEM.conflict) return t('sell.pay.conflict');
  return problem.text(error, { fallback: t('sell.pay.refused') });
}

/** Checkout in a centred dialog: the priced sale and its tenders until the total is covered. */
export function CheckoutDialog({
  sale,
  player,
  canOnAccount,
  organizationId,
  venueId,
  onSale,
  onClose,
  onDone,
}: {
  readonly sale: Sale | null;
  readonly player: OrganizationPlayer | null;
  readonly canOnAccount: boolean;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onSale: (sale: Sale) => void;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const paid = sale?.status === 'paid';
  return (
    <Dialog
      open={sale !== null}
      onOpenChange={(next) => {
        if (!next) (paid ? onDone : onClose)();
      }}
      size="md"
      title={t('sell.pay.title')}
    >
      {sale === null ? null : (
        <Checkout
          sale={sale}
          player={player !== null && sale.playerId === player.id ? player : null}
          canOnAccount={canOnAccount}
          organizationId={organizationId}
          venueId={venueId}
          onSale={onSale}
          onDone={onDone}
        />
      )}
    </Dialog>
  );
}

function Checkout({
  sale,
  player,
  canOnAccount,
  organizationId,
  venueId,
  onSale,
  onDone,
}: {
  readonly sale: Sale;
  /** The sale's player, when one was attached: they unlock the Wallet and On account tenders. */
  readonly player: OrganizationPlayer | null;
  readonly canOnAccount: boolean;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onSale: (sale: Sale) => void;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problem = useProblem();
  const format = useVenueFormatter();
  const api = useApi();
  const takePayment = useTakeSalePayment(organizationId, venueId);
  const takeOnAccount = useTakeSaleOnAccount(organizationId, venueId);
  const createIntent = useCreateSalePaymentIntent(organizationId, venueId);
  const walletBalance = useWalletBalance(organizationId, player?.id);
  const onlinePayments = useOnlinePayments(organizationId, venueId);
  const onlineOpen = onlinePayments.data?.enabled === true;
  const counterUpi = useCounterUpi(organizationId, venueId);
  /** The payment request the player is paying now, while the Online tender is in progress. */
  const [intent, setIntent] = useState<PaymentIntent | null>(null);

  const paid = sale.status === 'paid';
  const tenders: readonly CheckoutTender[] = [
    ...COUNTER_TENDERS,
    ...(onlineOpen ? (['online'] as const) : []),
    ...(player === null ? [] : (['wallet'] as const)),
    ...(player !== null && canOnAccount ? (['on_account'] as const) : []),
  ];
  // ADR-0031: only cash needs a shift — locked for a member holding no drawer, who is asked to start one.
  const cashLocked = useCashLocked({ id: venueId, organizationId });
  // A kiosk order paid by scanning the venue's own UPI QR at the PC (F-PAY-02) is confirmed with counter UPI.
  const [chosenTender, setTender] = useState<CheckoutTender>(sale.scanToPayReference !== undefined ? 'counter_upi' : 'cash');
  // The day's status may arrive after the dialog opened: a Cash tender that has just locked moves to counter UPI.
  const tender: CheckoutTender = cashLocked && chosenTender === 'cash' ? 'counter_upi' : chosenTender;
  const [amount, setAmount] = useState(minorToMajor(sale.outstanding.amount, sale.outstanding.currency));
  /** "Split payment": the amount field is shown, to take part of the balance now. */
  const [splitting, setSplitting] = useState(false);
  const [tendered, setTendered] = useState('');
  const [reference, setReference] = useState('');
  const [changeTo, setChangeTo] = useState<ChangeTo>('cash');
  /** The player picked for the change on a sale that names none (ADR-0049). */
  const [changePlayer, setChangePlayer] = useState<OrganizationPlayer | null>(null);
  const [credited, setCredited] = useState<{ readonly amount: number; readonly name: string } | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  // A refused amount shakes its own field, once per refusal (the error text is the message; nothing else moves).
  const [amountShakes, setAmountShakes] = useState(0);
  const [tenderedShakes, setTenderedShakes] = useState(0);
  const amountShake = useShake(amountShakes);
  const shake = useShake(tenderedShakes);

  const currency = sale.total.currency;
  const paying = moneyFrom(amount, currency);
  const tenderedMoney = tendered.trim() === '' ? null : moneyFrom(tendered, currency);
  const change = tender === 'cash' && paying !== null && tenderedMoney !== null ? changeDue(tenderedMoney.amount, paying.amount) : null;
  const short = tender === 'cash' && paying !== null && tenderedMoney !== null && tenderedMoney.amount < paying.amount;
  const lastChange = [...sale.payments].reverse().find((payment) => payment.change !== null)?.change ?? null;
  const hasChange = change !== null && change > 0;
  // The venue's answer on the sale read (ADR-0049): available, or why not. Absent reads as not available.
  const walletVenue = sale.changeToWalletAvailable === true;
  const walletPlayer = player ?? changePlayer;
  const toWallet = hasChange && walletVenue && changeTo === 'wallet';

  function resetTender(): void {
    setChangeTo('cash');
    setChangePlayer(null);
    setError(undefined);
  }

  function take(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const amountMoney = moneyFrom(amount, currency);
    if (amountMoney === null || amountMoney.amount <= 0) {
      setSplitting(true);
      setError(t('sell.pay.amountInvalid'));
      setAmountShakes((n) => n + 1);
      return;
    }
    const handedOver = tender === 'cash' && tendered.trim() !== '' ? moneyFrom(tendered, currency) : null;
    if (tender === 'cash' && tendered.trim() !== '' && (handedOver === null || handedOver.amount < amountMoney.amount)) {
      setError(handedOver === null ? t('sell.pay.amountInvalid') : t('sell.pay.tenderedShort'));
      setTenderedShakes((n) => n + 1);
      return;
    }
    if (toWallet && walletPlayer === null) {
      setError(t('sell.pay.changePickPlayer'));
      return;
    }
    setError(undefined);
    const credit = toWallet && change !== null && walletPlayer !== null ? { amount: change, name: playerName(walletPlayer) } : null;
    const callbacks = {
      onSuccess: (updated: Sale) => {
        if (credit !== null) setCredited((sum) => ({ amount: (sum?.amount ?? 0) + credit.amount, name: credit.name }));
        resetTender();
        onSale(updated);
        // The payment that settles the sale lands with the success toast; a part payment does not.
        if (updated.outstanding.amount === 0) show({ tone: 'ok', title: t('sell.pay.paidToast') });
        setAmount(minorToMajor(updated.outstanding.amount, updated.outstanding.currency));
        setTendered('');
        setReference('');
      },
      onError: (mutationError: unknown) => setError(refusalMessage(t, problem, mutationError)),
    };
    if (tender === 'on_account') {
      takeOnAccount.mutate({ saleId: sale.id, body: { amount: amountMoney } }, callbacks);
      return;
    }
    if (tender === 'online') {
      // A payment request, not a payment: the sale is paid when the provider confirms it (spec/06 §9.3).
      createIntent.mutate(
        { saleId: sale.id, amount: amountMoney },
        {
          onSuccess: setIntent,
          onError: (mutationError: unknown) =>
            setError(problem.text(mutationError, { fallback: t('onlinePayment.refused') })),
        },
      );
      return;
    }
    takePayment.mutate(
      {
        saleId: sale.id,
        body: {
          tender,
          amount: amountMoney,
          ...(handedOver === null ? {} : { tendered: handedOver }),
          ...(toWallet ? { changeToWallet: true, ...(player === null && changePlayer !== null ? { changeToWalletPlayerId: changePlayer.id } : {}) } : {}),
          // A reference on the venue's own machine or QR: the UPI reference, or the card's last 4 digits and slip
          // reference — never a card number (the server refuses one, spec/20 §5).
          ...((tender === 'counter_upi' || tender === 'counter_card') && reference.trim() !== '' ? { reference: reference.trim() } : {}),
        },
      },
      callbacks,
    );
  }

  // The provider answered the payment request. A capture changed the sale, so read it again; anything
  // else leaves the sale as it was, and the request stays on screen with its answer until dismissed.
  const onIntentSettled = useCallback(
    (settled: PaymentIntent) => {
      if (settled.status !== 'succeeded') return;
      void api.getSale({ path: { organizationId, venueId, saleId: sale.id } }).then((updated) => {
        onSale(updated);
        setIntent(null);
        setAmount(minorToMajor(updated.outstanding.amount, updated.outstanding.currency));
      });
    },
    [api, organizationId, venueId, sale.id, onSale],
  );

  const partlyPaid = sale.paid.amount > 0 && !paid;
  const busy = takePayment.isPending || takeOnAccount.isPending || createIntent.isPending;
  const payingLabel = paying === null ? '' : format.money(paying);
  const takeLabel =
    tender === 'on_account'
      ? t('sell.pay.takeOnAccount')
      : tender === 'online'
        ? t('sell.pay.requestOnline')
        : tender === 'counter_upi' && counterUpi.data?.vpa != null
          ? t('sell.pay.confirmUpi')
          : tender === 'counter_card'
            ? t('sell.pay.confirmCard')
            : tender === 'cash' && paying !== null
              ? t('sell.pay.takeCash', { amount: payingLabel })
              : t('sell.pay.take');

  return (
    <div className="console-checkout console-checkout--compact">
      {/* One line of figures: what is due large, the subtotal and GST quiet beside it. */}
      <div className="console-checkout__due" role="group" aria-label={t('sell.pay.summary')}>
        <div className="console-checkout__due-main">
          <span className="console-checkout__due-label">{paid ? t('sell.pay.total') : partlyPaid ? t('sell.pay.stillToPay') : t('sell.pay.total')}</span>
          <span className="console-checkout__amount clav-figure">
            <RollingNumber
              value={(paid ? sale.total : sale.outstanding).amount}
              format={(amount) => format.money({ amount, currency: sale.total.currency })}
            />
          </span>
        </div>
        <p className="console-checkout__breakdown console-num">
          {t('sell.pay.breakdown', { subtotal: format.money(sale.subtotal), gst: format.money(sale.tax) })}
          {sale.discount.amount > 0 ? ` · ${t('sell.pay.discountShort', { amount: format.money(sale.discount) })}` : ''}
          {partlyPaid ? ` · ${t('sell.pay.paidOf', { paid: format.money(sale.paid), total: format.money(sale.total) })}` : ''}
        </p>
      </div>

      <StepSlide step={paid ? 'paid' : intent !== null ? 'online' : 'tender'}>
      {paid ? (
        <div className="console-checkout-done clav-receipt-in">
          {/* Paid lands once: the ember ring bursts around the tick, then the receipt rolls in line by line. */}
          <div className="console-checkout-done__burst" aria-hidden="true">
            <Impact burst="paid" size={64}>
              <CheckIcon width={28} height={28} />
            </Impact>
          </div>
          <Notice tone="ok" role="status" title={t('sell.pay.paidBadge')}>
            {credited !== null
              ? t('sell.pay.changeToWalletDone', { amount: format.money({ amount: credited.amount, currency }), name: credited.name })
              : lastChange !== null && lastChange.amount > 0
                ? t('sell.pay.change', { amount: format.money(lastChange) })
                : t('sell.pay.paidNoChange')}
          </Notice>
          {/* A guest pass sold to nobody's account: its ticket code, big, for the guest to take to a PC. */}
          {sale.playerId === null && sale.lines.some((line) => line.passProductId !== null) ? (
            <GuestTicketCodes organizationId={organizationId} venueId={venueId} saleId={sale.id} />
          ) : null}
          <Button variant="primary" size="lg" block onClick={onDone}>
            {t('sell.pay.newSale')}
          </Button>
        </div>
      ) : intent !== null ? (
        <OnlinePayment organizationId={organizationId} venueId={venueId} intent={intent} onSettled={onIntentSettled} onAgain={() => setIntent(null)} />
      ) : (
        <form className="console-form console-checkout__form" aria-label={t('sell.pay.tenderLabel')} noValidate onSubmit={take}>
          {error !== undefined ? (
            <Notice tone="danger" role="alert">
              {error}
            </Notice>
          ) : null}
          <SegmentedControl<CheckoutTender>
            label={t('sell.pay.tender')}
            value={tender}
            block
            className="console-checkout__methods"
            onValueChange={(value) => {
              setTender(value);
              resetTender();
            }}
            options={tenders.map((value) => ({
              value,
              label: t(`sell.tenderShort.${value}`),
              icon: <TenderIcon tender={value} />,
              disabled: value === 'cash' && cashLocked,
            }))}
          />
          {cashLocked ? <CashLockedNotice /> : null}
          {player === null ? null : <p className="console-subtle console-checkout__for">{t('sell.pay.forPlayer', { name: playerName(player) })}</p>}

          {splitting ? (
            <div key={amountShake.key} className={amountShake.className}>
            <TextField
              label={t('sell.pay.amount', { currency })}
              required
              numeric
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              description={t('sell.pay.splitHint', { amount: format.money(sale.outstanding) })}
            />
            </div>
          ) : null}

          {tender === 'cash' ? (
            <div className="console-cash">
              <div key={shake.key} className={shake.className}>
              <TextField
                label={t('sell.pay.tendered', { currency })}
                showOptional
                numeric
                inputMode="decimal"
                value={tendered}
                error={short ? t('sell.pay.tenderedShort') : undefined}
                onChange={(event) => {
                  setTendered(event.target.value);
                  setError(undefined);
                }}
              />
              </div>
              {/* One tap for what a player usually hands over: the amount exactly, then the next round notes. */}
              {paying !== null && paying.amount > 0 ? (
                <div className="console-quick-tender" role="group" aria-label={t('sell.pay.quickLabel')}>
                  {[paying.amount, ...cashSuggestions(paying.amount, currency)].map((value, index) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-pressed={tenderedMoney?.amount === value}
                      onClick={() => setTendered(minorToMajor(value, currency))}
                    >
                      {index === 0 ? t('sell.pay.exact') : format.money({ amount: value, currency }, 'noFraction')}
                    </Button>
                  ))}
                </div>
              ) : null}
              {hasChange && change !== null ? (
                <ChangeChoice
                  sale={sale}
                  organizationId={organizationId}
                  change={{ amount: change, currency }}
                  player={player}
                  picked={changePlayer}
                  onPick={setChangePlayer}
                  value={walletVenue ? changeTo : 'cash'}
                  onValueChange={(next) => {
                    setChangeTo(next);
                    setError(undefined);
                  }}
                />
              ) : null}
            </div>
          ) : tender === 'counter_card' ? (
            <TextField label={t('sell.pay.cardReference')} showOptional value={reference} onChange={(event) => setReference(event.target.value)} description={t('sell.pay.cardReferenceHint')} />
          ) : tender === 'counter_upi' ? (
            <>
              {counterUpi.data?.vpa != null && paying !== null && paying.amount > 0 ? (
                <CounterUpiQr vpa={counterUpi.data.vpa} payeeName={counterUpi.data.payeeName} amount={paying} note={t('sell.pay.upiNote', { venue: counterUpi.data.payeeName })} />
              ) : null}
              <TextField label={t('sell.pay.reference')} showOptional value={reference} onChange={(event) => setReference(event.target.value)} description={t('sell.pay.referenceHint')} />
            </>
          ) : tender === 'online' ? (
            <p className="console-subtle">{t('sell.pay.onlineHint')}</p>
          ) : tender === 'wallet' ? (
            <p className="console-subtle console-num" role="status">
              {walletBalance.data === undefined ? t('sell.pay.walletBalanceLoading') : t('sell.pay.walletBalance', { amount: format.money(walletBalance.data.total) })}
            </p>
          ) : player === null ? null : (
            <p className="console-subtle">{t('sell.pay.onAccountHint', { name: playerName(player) })}</p>
          )}

          {/* The less common steps, each a quiet link that opens its field in place. */}
          <div className="console-checkout__links">
            {splitting ? null : (
              <button type="button" className="console-linkbutton" onClick={() => setSplitting(true)}>
                {t('sell.pay.split')}
              </button>
            )}
            <CheckoutBuyer sale={sale} organizationId={organizationId} venueId={venueId} onSale={onSale} />
            <CheckoutCodes
              sale={sale}
              player={player}
              organizationId={organizationId}
              venueId={venueId}
              onSale={(updated) => {
                onSale(updated);
                setAmount(minorToMajor(updated.outstanding.amount, updated.outstanding.currency));
              }}
            />
          </div>

          <div className="console-checkout__take">
            <Button type="submit" variant="primary" size="lg" block busy={busy} disabled={short}>
              {takeLabel}
            </Button>
          </div>
        </form>
      )}
      </StepSlide>
    </div>
  );
}

/**
 * The change from a cash overpayment (ADR-0036, ADR-0049): "Give it back in cash" (the default) or "Add it to a
 * player's wallet". With nobody on the sale, choosing the wallet opens a player search right here. Where the venue
 * cannot take change into a wallet, the wallet choice is switched off and says which setting turns it on.
 */
function ChangeChoice({
  sale,
  organizationId,
  change,
  player,
  picked,
  onPick,
  value,
  onValueChange,
}: {
  readonly sale: Sale;
  readonly organizationId: string;
  readonly change: Money;
  readonly player: OrganizationPlayer | null;
  readonly picked: OrganizationPlayer | null;
  readonly onPick: (player: OrganizationPlayer | null) => void;
  readonly value: ChangeTo;
  readonly onValueChange: (value: ChangeTo) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useConsoleScope();
  const amount = format.money(change);
  const available = sale.changeToWalletAvailable === true;
  const holder = player ?? picked;
  const walletLabel = player !== null ? t('sell.pay.changeToNamedWallet', { amount, name: playerName(player) }) : t('sell.pay.changeToAWallet', { amount });
  const orgSlug = scope.organization?.slug;
  return (
    <div className="console-change">
      <p className="console-change__due" role="status">
        <span>{t('sell.pay.changeDue')}</span>
        <strong className="clav-figure">{amount}</strong>
      </p>
      <SegmentedControl<ChangeTo>
        label={t('sell.pay.changeLabel')}
        value={value}
        onValueChange={onValueChange}
        block
        size="sm"
        className="console-change__choice"
        options={[
          { value: 'cash', label: t('sell.pay.changeBackInCash', { amount }), icon: <Icon name="cash" /> },
          { value: 'wallet', label: walletLabel, icon: <NavIcon name="wallet" />, disabled: !available },
        ]}
      />
      {!available ? (
        <p className="console-subtle console-change__off">
          {sale.changeToWalletUnavailable === 'top_ups_taxed_at_sale' ? t('sell.pay.changeWalletTaxedAtSale') : t('sell.pay.changeWalletOff')}{' '}
          {orgSlug === undefined ? null : (
            <Link to="/o/$orgSlug/settings/$group" params={{ orgSlug, group: 'money' }} hash={sale.changeToWalletUnavailable === 'top_ups_taxed_at_sale' ? 'tax.walletTopupTaxPoint' : 'wallet.enabled'}>
              {t('sell.pay.changeWalletSettings')}
            </Link>
          )}
        </p>
      ) : value === 'wallet' && player === null ? (
        holder !== null ? (
          <div className="console-player-chosen console-player-chosen--compact">
            <Avatar name={playerName(holder)} size="sm" decorative />
            <span className="console-player-chosen__name">{t('sell.pay.changeGoesTo', { name: playerName(holder) })}</span>
            <Button type="button" variant="ghost" size="sm" aria-label={t('sell.cart.playerRemove', { name: playerName(holder) })} onClick={() => onPick(null)}>
              {t('sell.cart.playerChange')}
            </Button>
          </div>
        ) : (
          <ChangePlayerPicker organizationId={organizationId} onPick={onPick} />
        )
      ) : null}
    </div>
  );
}

/** Find the player whose wallet takes the change, by name, username, phone or member code: up to four matches. */
function ChangePlayerPicker({ organizationId, onPick }: { readonly organizationId: string; readonly onPick: (player: OrganizationPlayer) => void }): ReactNode {
  const { t } = useTranslation('console');
  const [q, setQ] = useState('');
  const search = usePlayerSearch(organizationId, q, { enabled: q.trim().length >= PLAYER_SEARCH_MIN });
  const results = pickerResults(search, q).slice(0, 4);
  return (
    <div className="console-change__pick">
      <SearchField label={t('sell.pay.changeFindPlayer')} placeholder={t('sell.cart.playerHint')} value={q} onChange={(event) => setQ(event.target.value)} />
      {search.isFetching ? <Skeleton shape="text" lines={1} /> : null}
      {results.length > 0 ? (
        <ul className="console-player-pick__results console-change__results">
          {results.map((candidate) => (
            <li key={candidate.id}>
              <button type="button" className="console-player-pick__result" onClick={() => onPick(candidate)}>
                <Avatar name={playerName(candidate)} size="sm" decorative />
                <span className="console-player-pick__name">{playerName(candidate)}</span>
                {candidate.phoneNumber === null ? null : <span className="console-subtle">{candidate.phoneNumber}</span>}
              </button>
            </li>
          ))}
        </ul>
      ) : q.trim().length >= PLAYER_SEARCH_MIN && search.data !== undefined && !search.isFetching ? (
        <p className="console-subtle">{t('sell.pay.changeNoPlayer')}</p>
      ) : null}
    </div>
  );
}

/**
 * The venue's own UPI QR for the amount due (F-PAY-02, spec/06 §8.3): a `upi://pay` intent the player scans with any
 * UPI app. The money goes straight to the venue. Nothing is recorded until staff confirm the app approved it — the
 * take button says so.
 */
function CounterUpiQr({ vpa, payeeName, amount, note }: { readonly vpa: string; readonly payeeName: string; readonly amount: Money; readonly note: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const code = useMemo(() => {
    try {
      return encodeQr(counterUpiIntent(vpa, payeeName, amount.amount, note));
    } catch {
      return null;
    }
  }, [vpa, payeeName, amount.amount, note]);
  if (code === null) return null;
  return (
    <figure className="sell-upi-qr" aria-label={t('sell.pay.upiQrLabel', { amount: format.money(amount) })}>
      <svg viewBox={`-4 -4 ${code.size + 8} ${code.size + 8}`} shapeRendering="crispEdges" role="img" aria-hidden="true" focusable="false">
        <rect x="-4" y="-4" width={code.size + 8} height={code.size + 8} fill="#fff" />
        <path d={qrPath(code)} fill="#000" />
      </svg>
      <figcaption>
        <strong className="clav-figure">{format.money(amount)}</strong>
        <span className="console-subtle">{t('sell.pay.upiTo', { vpa })}</span>
        <span className="console-subtle">{t('sell.pay.upiConfirmHint')}</span>
      </figcaption>
    </figure>
  );
}

/**
 * Bill the sale to a business (F-CMP-07): its GSTIN and name, before the first tender. A GSTIN from another state than
 * the venue's makes the tax invoice charge IGST instead of CGST + SGST — the same tax, so the total does not move.
 * A small link until it is opened; the fields open in place.
 */
function CheckoutBuyer({ sale, organizationId, venueId, onSale }: { readonly sale: Sale; readonly organizationId: string; readonly venueId: string; readonly onSale: (sale: Sale) => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const setBuyer = useSetSaleBuyer(organizationId, venueId);
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ gstin?: string; name?: string }>({});
  const [gstin, setGstin] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  if (sale.buyer.gstin !== null) {
    return (
      <p className="console-subtle sell-buyer console-checkout__wide" role="status">
        {t('sell.buyer.billedTo', { name: sale.buyer.name ?? '', gstin: sale.buyer.gstin })}
        {sale.buyer.placeOfSupply === 'inter_state' ? ` · ${t('sell.buyer.igst')}` : ''}
      </p>
    );
  }
  // Before the first tender only: after it the tax is in the ledger.
  if (sale.paid.amount > 0) return null;
  if (!open) {
    return (
      <button type="button" className="console-linkbutton" aria-expanded={false} onClick={() => setOpen(true)}>
        {t('sell.buyer.add')}
      </button>
    );
  }

  function save(): void {
    const typed = gstin.trim().toUpperCase();
    if (!/^[0-9]{2}[A-Z0-9]{13}$/.test(typed) || name.trim() === '') {
      setError(t('sell.buyer.invalid'));
      return;
    }
    setError(undefined);
    setFieldErrors({});
    setBuyer.mutate(
      { saleId: sale.id, gstin: typed, name: name.trim() },
      {
        onSuccess: (updated) => {
          onSale(updated);
          setOpen(false);
        },
        onError: (mutationError) => {
          // A validation failure names the field: shown beside it. Anything else is the form's.
          const gstinProblem = problem.fieldMessage(mutationError, 'gstin');
          const nameProblem = problem.fieldMessage(mutationError, 'name');
          if (gstinProblem !== undefined || nameProblem !== undefined) {
            setFieldErrors({ ...(gstinProblem === undefined ? {} : { gstin: gstinProblem }), ...(nameProblem === undefined ? {} : { name: nameProblem }) });
            return;
          }
          setError(problemCode(mutationError) === PROBLEM.validationFailed ? t('sell.buyer.invalid') : problem.text(mutationError, { fallback: t('sell.buyer.refused') }));
        },
      },
    );
  }

  // Inside checkout's own form, so its fields are a group, not a nested form: Save is a plain button.
  return (
    <div className="console-checkout__inline console-checkout__wide" role="group" aria-label={t('sell.buyer.title')}>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <div className="console-form__row">
        <TextField label={t('sell.buyer.gstin')} required autoComplete="off" error={fieldErrors.gstin} value={gstin} onChange={(event) => setGstin(event.target.value)} />
        <TextField label={t('sell.buyer.name')} required error={fieldErrors.name} value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="console-form__actions">
        <Button type="button" variant="secondary" size="sm" busy={setBuyer.isPending} onClick={save}>
          {t('sell.buyer.save')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          {t('sell.buyer.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * A discount code for the sale (F-POS-02), before any tender is taken — at most one per sale — and,
 * with a player on it, a coupon to redeem for them (F-POS-06). Each a small link that opens one field in place.
 */
function CheckoutCodes({
  sale,
  player,
  organizationId,
  venueId,
  onSale,
}: {
  readonly sale: Sale;
  readonly player: OrganizationPlayer | null;
  readonly organizationId: string;
  readonly venueId: string;
  readonly onSale: (sale: Sale) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const problem = useProblem();
  const applyCode = useApplySaleCode(organizationId, venueId);
  const redeem = useRedeemCoupon(organizationId, venueId);
  const [open, setOpen] = useState<'code' | 'coupon' | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [granted, setGranted] = useState<string | undefined>(undefined);

  const applied = sale.discounts.find((discount) => discount.kind === 'code');
  const canCode = applied === undefined && sale.payments.length === 0;
  if (!canCode && player === null && applied === undefined) return null;

  function toggle(which: 'code' | 'coupon'): void {
    setOpen(open === which ? null : which);
    setText('');
    setError(undefined);
  }

  function submit(): void {
    const value = text.trim().toUpperCase();
    if (value === '') {
      setError(t('sell.codes.required'));
      return;
    }
    setError(undefined);
    if (open === 'code') {
      applyCode.mutate(
        { saleId: sale.id, code: value },
        {
          onSuccess: (updated) => {
            onSale(updated);
            setOpen(null);
          },
          onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.notFound ? t('sell.codes.unknown') : problem.text(mutationError, { fallback: t('sell.codes.refused') })),
        },
      );
      return;
    }
    if (player === null) return;
    redeem.mutate(
      { playerId: player.id, code: value },
      {
        onSuccess: (redemption) => {
          setGranted(t('sell.codes.granted', { amount: redemption.grantAmount === null ? '' : format.money(redemption.grantAmount), name: playerName(player) }));
          setOpen(null);
        },
        onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.notFound ? t('sell.codes.couponUnknown') : problem.text(mutationError, { fallback: t('sell.codes.couponRefused') })),
      },
    );
  }

  return (
    <>
      {canCode ? (
        <button type="button" className="console-linkbutton" aria-expanded={open === 'code'} onClick={() => toggle('code')}>
          {t('sell.codes.addCode')}
        </button>
      ) : null}
      {player !== null ? (
        <button type="button" className="console-linkbutton" aria-expanded={open === 'coupon'} onClick={() => toggle('coupon')}>
          {t('sell.codes.redeemCoupon')}
        </button>
      ) : null}
      {applied !== undefined ? (
        <p className="console-subtle console-checkout__wide" role="status">
          {t('sell.codes.applied', { code: applied.code ?? '', amount: format.money(applied.amount) })}
        </p>
      ) : null}
      {granted !== undefined ? (
        <p className="console-subtle console-checkout__wide" role="status">
          {granted}
        </p>
      ) : null}
      {open !== null ? (
        <div className={cx('console-checkout-codes__form', 'console-checkout__wide')} role="group" aria-label={open === 'code' ? t('sell.codes.addCode') : t('sell.codes.redeemCoupon')}>
          <TextField
            label={open === 'code' ? t('sell.codes.codeLabel') : t('sell.codes.couponLabel')}
            autoComplete="off"
            spellCheck={false}
            value={text}
            onChange={(event) => setText(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
            error={error}
          />
          <Button type="button" variant="secondary" busy={applyCode.isPending || redeem.isPending} onClick={submit}>
            {open === 'code' ? t('sell.codes.apply') : t('sell.codes.redeem')}
          </Button>
        </div>
      ) : null}
    </>
  );
}
